import React, { useState, useEffect, useRef } from 'react';
import useClickOutside from '../../hooks/useClickOutsideHandler';
import Icon from '../../common/components/Icon';

interface BreadcrumbsProps {
  firstHasPreIcon?: boolean;
  breadcrumbs: Breadcrumb[];
}

type Breadcrumb = {
  label: string;
  path: string;
};

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ firstHasPreIcon, breadcrumbs }) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [visibleItemsCount, setVisibleItemsCount] = useState<number>(3);

  const toggleDropdown = () => {
    setIsDropdownVisible((prevState) => !prevState);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 575) {
        setVisibleItemsCount(2);
      } else if (window.innerWidth < 768) {
        setVisibleItemsCount(3);
      } else if (window.innerWidth < 991) {
        setVisibleItemsCount(4);
      } else {
        setVisibleItemsCount(5);
      }
    };

    handleResize(); // Call the function initially

    window.addEventListener('resize', handleResize); // Add event listener for resize

    return () => {
      window.removeEventListener('resize', handleResize); // Clean up the event listener on component unmount
    };
  }, []);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useClickOutside(dropdownRef, () => {
    setIsDropdownVisible(false);
  });

  const visibleBreadcrumbs = breadcrumbs.slice(0, -1);
  const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];

  return (
    <nav className="breadcrumbs" aria-label="breadcrumbs">
      {/* Breadcrumb List */}
      <ol className="breadcrumbs__list">
        {visibleBreadcrumbs.slice(0, visibleItemsCount).map((crumb, index) => (
          <li key={crumb.label} className="breadcrumbs__item">
            {/* Render Home icon for the first breadcrumb */}
            {index === 0 && firstHasPreIcon && <Icon name="forbes-f-logo" size="small" />}
            <a href={crumb.path} className="link-14">
              {crumb.label}
            </a>
            {/* Custom separator */}
            <span className="breadcrumbs__separator link-14">/</span>
          </li>
        ))}
      </ol>

      {visibleBreadcrumbs.length > visibleItemsCount && (
        <div ref={dropdownRef} className="breadcrumbs__item">
          <div className="breadcrumbs__overflow-indicator">
            <button className="breadcrumbs__button" onClick={toggleDropdown}>
              ...
            </button>
            <span className="breadcrumbs__separator link-14">/</span>
            <ol className={`breadcrumbs__dropdown ${isDropdownVisible ? 'isVisible' : ''}`}>
              {visibleBreadcrumbs.slice(visibleItemsCount).map((crumb) => (
                <li key={crumb.label} className="breadcrumbs__item">
                  <a href={crumb.path} className="link-14">
                    {crumb.label}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}

      {/* Render the last breadcrumb separately */}
      {lastBreadcrumb && (
        <span className="breadcrumbs__item breadcrumbs__item--last link-14">{lastBreadcrumb.label}</span>
      )}
    </nav>
  );
};

export default Breadcrumbs;
